/**
 * What a save's outcome is (S 2.8): the rules behind the shared save helper (`useSave` in ./save), kept free of React so they are tested on
 * their own (scripts/dev/test/save-feedback.test.mjs).
 *
 * A save succeeded only when the API answered 2xx with its own answer – the stored record as JSON, or its explicit "no content" for actions
 * that return nothing – and that answer does not itself say the change was refused. Everything else is a failure with a cause the officer or
 * applicant can act on: offline, the server unreachable, the session ended, no permission, the record changed or gone, the data refused,
 * the server failing, or an answer that does not confirm the save.
 */

export type SaveCause =
  | "offline"
  | "network"
  | "session"
  | "forbidden"
  | "notFound"
  | "conflict"
  | "invalid"
  | "tooLarge"
  | "busy"
  | "server"
  | "unconfirmed"
  | "refused"
  | "cancelled";

/** What a successful answer must carry: the stored `record`, the API's `answer` of any kind (JSON or 204), or `nothing` to check. */
export type SaveExpectation = "record" | "answer" | "any";

export interface SaveFailure {
  cause: SaveCause;
  /** The HTTP status, 0 when no answer came back. */
  status: number;
  /** The server's own words (its problem title, detail or validation messages), when it gave any. */
  detail: string | null;
}

/** A save the helper itself judged failed although the request returned: not confirmed by the answer, or refused in it. */
export class SaveError extends Error {
  readonly cause: SaveCause;
  readonly status: number;
  readonly detail: string | null;
  /** The answer that said the change was refused (`{ succeeded: false, … }`), for screens that offer the next step. */
  readonly result: unknown;

  constructor(cause: SaveCause, detail: string | null, status = 200, result?: unknown) {
    super(detail ?? cause);
    this.name = "SaveError";
    this.cause = cause;
    this.status = status;
    this.detail = detail;
    this.result = result;
  }
}

interface ProblemLike {
  title?: string;
  detail?: string;
  error?: string;
  /** The Arabic of `error`, which some endpoints answer beside it. */
  errorAr?: string;
  code?: string;
  message?: string;
  errors?: Record<string, string[]>;
  issues?: Array<{ message?: string; messageAr?: string | null }>;
}

const SESSION_CODES = new Set(["token_expired", "session_idle", "session_expired", "session_revoked", "refresh_reuse", "session"]);
const GENERIC = /^\d{3}(\s|$)|^(Failed to fetch|Load failed|NetworkError when attempting to fetch resource\.?)$/i;

function text(v: unknown): string | null {
  return typeof v === "string" && v.trim() ? v.trim() : null;
}

/** The server's own words in a problem document: its validation messages first, then its detail and title. */
export function problemDetail(problem: ProblemLike | undefined | null, arabic = false): string | null {
  if (!problem || typeof problem !== "object") return null;
  const issues = (problem.issues ?? []).map((i) => text(arabic ? (i.messageAr ?? i.message) : i.message)).filter((m): m is string => !!m);
  const fields = Object.values(problem.errors ?? {}).flat().map(text).filter((m): m is string => !!m);
  const listed = [...new Set([...issues, ...fields])];
  const own = text(problem.detail) ?? (arabic ? text(problem.errorAr) : null) ?? text(problem.error) ?? text(problem.message);
  const title = text(problem.title);
  const parts = [listed.length ? listed.join(" · ") : null, own, !listed.length && !own ? title : null].filter((p): p is string => !!p && !GENERIC.test(p));
  return parts.length ? [...new Set(parts)].join(" — ") : null;
}

function causeOfStatus(status: number, code: string | undefined): SaveCause {
  if (code === "offline") return "offline";
  if (code === "network") return "network";
  if (code === "not_api" || code === "empty_answer") return "unconfirmed";
  if (status === 401 || (code && SESSION_CODES.has(code))) return "session";
  if (status === 403) return "forbidden";
  if (status === 404 || status === 410) return "notFound";
  if (status === 409 || status === 412) return "conflict";
  if (status === 400 || status === 422) return "invalid";
  if (status === 413) return "tooLarge";
  if (status === 429) return "busy";
  if (status === 0) return "network";
  if (status >= 200 && status < 300) return "unconfirmed";
  return "server";
}

/** The cause of any error a save can end with: the API client's errors, the helper's own, the browser's network and abort errors. */
export function saveFailure(error: unknown, arabic = false): SaveFailure {
  if (error instanceof SaveError) return { cause: error.cause, status: error.status, detail: error.detail };
  if (error && typeof error === "object") {
    const e = error as { name?: string; status?: unknown; problem?: ProblemLike; message?: string };
    if (e.name === "AbortError") return { cause: "cancelled", status: 0, detail: null };
    if (typeof e.status === "number") {
      const code = e.problem?.code;
      const cause = causeOfStatus(e.status, code);
      // The transport's own message names the cause already; the server's words are kept for every answer it gave.
      const detail = cause === "offline" || cause === "network" ? null : (problemDetail(e.problem, arabic) ?? (text(e.message) && !GENERIC.test(e.message!.trim()) ? e.message!.trim() : null));
      return { cause, status: e.status, detail };
    }
    // fetch's own TypeError when the request could not be sent (a transport other than the API client's).
    if (e.name === "TypeError" && GENERIC.test(e.message?.trim() ?? "")) {
      return { cause: typeof navigator !== "undefined" && navigator.onLine === false ? "offline" : "network", status: 0, detail: null };
    }
    return { cause: "server", status: 0, detail: text(e.message) };
  }
  return { cause: "server", status: 0, detail: text(error) };
}

/**
 * Checks the answer of a save before anything reads as saved. `record` wants the stored record back; `answer` accepts the API's 204 too;
 * `any` checks only that the answer does not say it was refused. A text answer (the body was not the API's JSON) is never a stored record;
 * an empty 200 to a write never gets here (the API client refuses it as `empty_answer`). A step the stored change was refused is not a
 * refusal of the change itself: see `refusedStep`.
 */
export function confirmAnswer<T>(result: T, expect: SaveExpectation = "answer"): T {
  if (result && typeof result === "object" && !Array.isArray(result)) {
    // `succeeded: false` is the platform's refusal answer (a workflow transition, an approval the gate held): 200, nothing changed.
    const r = result as { succeeded?: unknown; error?: unknown; message?: unknown; reason?: unknown; refusal?: { message?: unknown } | null };
    if (r.succeeded === false) {
      throw new SaveError("refused", text(r.refusal?.message) ?? text(r.error) ?? text(r.message) ?? text(r.reason), 200, result);
    }
    return result;
  }
  if (expect === "any") return result;
  if (typeof result === "string") throw new SaveError("unconfirmed", null);
  if (expect === "record" && (result === undefined || result === null)) throw new SaveError("unconfirmed", null, 204);
  return result;
}

/**
 * The step a stored change was to take and was refused (S 2.8): completing a task is stored, and the workflow step it takes may still be
 * refused – the answer carries `transition: { succeeded: false, error }` (a case's task) or `transitionError` (a matter's task). The change
 * is saved and is confirmed as saved; the refused step is reported beside it, with the server's reason. Null when no step was refused.
 */
export function refusedStep(result: unknown): { reason: string | null } | null {
  if (!result || typeof result !== "object" || Array.isArray(result)) return null;
  const r = result as { transition?: unknown; transitionError?: unknown };
  if (r.transition && typeof r.transition === "object" && (r.transition as { succeeded?: unknown }).succeeded === false) {
    const t = r.transition as { error?: unknown; message?: unknown };
    return { reason: text(t.error) ?? text(t.message) };
  }
  const own = text(r.transitionError);
  return own ? { reason: own } : null;
}

/** Whether the failure is worth a message at all: a request the screen itself cancelled is not a failure. */
export function isReportable(failure: SaveFailure): boolean {
  return failure.cause !== "cancelled";
}
